import { apiBaseUrl } from '@/shared/env'
import { getWarehouseDevice } from '@/shared/auth/warehouseDeviceStorage'
import { clearWarehouseSession, getWarehouseSession } from '@/shared/auth/warehouseSessionStorage'
import { getDelmanDevice } from '@/shared/auth/delmanDeviceStorage'
import { clearDelmanSession, getDelmanSession } from '@/shared/auth/delmanSessionStorage'

const baseUrl = apiBaseUrl

function isWrongPinError(message: string): boolean {
  const lower = message.toLowerCase()
  return lower.includes('pin salah') || lower.includes('terlalu banyak percobaan')
}

function isDelmanApp(): boolean {
  return typeof window !== 'undefined' && window.location.pathname.startsWith('/delman')
}

function getAccessToken(): string | null {
  if (typeof window === 'undefined') return null
  return isDelmanApp() ? (getDelmanSession()?.accessToken ?? null) : (getWarehouseSession()?.accessToken ?? null)
}

function getActiveDeviceId(): string | null {
  if (typeof window === 'undefined') return null
  return isDelmanApp() ? (getDelmanDevice()?.deviceId ?? null) : (getWarehouseDevice()?.deviceId ?? null)
}

/** Auth header + X-Device-Id — rute terproteksi warehouse-server wajib kedua header ini (middleware.RequireDeviceHeader). */
function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {}
  const token = getAccessToken()
  const deviceId = getActiveDeviceId()
  if (token) headers.Authorization = `Bearer ${token}`
  if (deviceId) headers['X-Device-Id'] = deviceId
  return headers
}

/**
 * Token kedaluwarsa / tidak valid → bersihkan sesi sesuai konteks dan arahkan ke
 * halaman login yang tepat. Tidak dijalankan di server, saat sudah di halaman
 * login/activate, atau untuk permintaan auth (mis. PIN salah saat login).
 */
function handleUnauthorized(): void {
  if (typeof window === 'undefined') return
  const path = window.location.pathname
  const onAuthPage = path.includes('/login') || path.includes('/activate')

  if (isDelmanApp()) {
    clearDelmanSession()
    if (!onAuthPage) window.location.href = '/delman/login'
  } else {
    clearWarehouseSession()
    if (!onAuthPage) window.location.href = '/warehouse/login'
  }
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const isFormData = init?.body instanceof FormData
  const res = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...authHeaders(),
      ...init?.headers,
    },
  })

  if (!res.ok) {
    let message = `API ${res.status}: ${res.statusText}`
    try {
      const body = (await res.json()) as { error?: string }
      if (body.error) message = body.error
    } catch {
      // keep default message
    }

    // Hanya logout saat token/sesi benar-benar invalid — bukan PIN salah di layar login.
    if (res.status === 401 && !path.startsWith('/auth') && !isWrongPinError(message)) {
      handleUnauthorized()
    }

    throw new Error(message)
  }

  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}
